import { Locale } from '@tuic/i18n';

/**
 * Transforms a given string to a slug.
 * Optionally replaces ampersands with locale-specific replacements.
 *
 * @param str - The string to slugify
 * @param options - Optional configuration
 * @param options.replaceAmpersand - Whether to replace ampersands (default: false)
 * @param options.locale - Locale to use for ampersand replacement (default: 'de')
 * @returns The slugified string
 */
export const slugify = (
  str: string,
  options?: {
    replaceAmpersand?: boolean;
    locale?: Locale;
  },
): string => {
  let result = str;

  // Replace ampersands with locale-specific replacements
  if (options?.replaceAmpersand) {
    const locale = options.locale || 'de';
    const replacement = locale === 'de' ? 'und' : 'and';
    result = result.replace(/&/g, replacement);
  }

  // Replace dashes with spaces for consistent processing
  result = result.split('-').join(' ');

  // Convert to lowercase and trim whitespace
  result = result.toLowerCase().trim();

  // Handle German / Scandinavian special characters
  const special = {
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    ß: 'ss',
    ø: 'o',
    å: 'a',
    æ: 'ae',
  };

  (Object.keys(special) as Array<keyof typeof special>).forEach(key => {
    result = result.split(key).join(special[key]);
  });

  // Normalize special characters (e.g., é -> e)
  result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Remove non-alphanumeric characters and replace spaces with hyphens
  result = result
    .replace(/[^a-z0-9 -]/g, '') // Remove any non-alphanumeric characters
    .replace(/\s+/g, '-'); // Replace spaces with hyphens

  return result;
};
